import { useCallback, useState } from 'react';
import { useAsync } from 'react-use';
import { approveMember, getBusinessMembers, removeMember } from '../../data-layer/business/service';
import { BusinessMemberModel } from '../../data-layer/business/types';
import { showNotificationAsync } from '../../data-layer/notifications/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { BusinessMemberStatus } from '../../types';
import { AlertRejectedError, confirmAlert } from '../../ui/feedback/Alert';
import { canEditBusiness } from '../../utils/accessService';
import { ymTrackSettingsRemoveAdmin } from '../../utils/metrika';
import { useTranslation } from '../../utils/useTranslation';
import { useCurrentUser } from '../users/useCurrentUser';

export const useBusinessMembers = (businessId: string) => {
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const t = useTranslation();
    const [members, setMembers] = useState<BusinessMemberModel[]>([]);

    const onRemove = useCallback(
        async (memberId: string) => {
            try {
                ymTrackSettingsRemoveAdmin({
                    page: 'Настройки бизнеса',
                    userLogin: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                });

                if (canEditBusiness(businessId, currentUser)) {
                    await confirmAlert(
                        'Удаление',
                        'Вы действительно хотите удалить администратора?',
                    );
                    await removeMember(businessId, memberId);
                    setMembers(members.filter((member) => member.memberId !== memberId));
                } else {
                    dispatch(showNotificationAsync(t('Вы не можете удалить администратора')));
                }
            } catch (error) {
                if (!(error instanceof AlertRejectedError)) {
                    dispatch(showNotificationAsync(t('Не удалось удалить администратора')));
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [businessId, dispatch, members, setMembers, currentUser],
    );

    const onApprove = useCallback(
        async (memberId: string) => {
            try {
                if (canEditBusiness(businessId, currentUser)) {
                    await approveMember(businessId, memberId);
                    setMembers(
                        members.map((businessMember) => ({
                            ...businessMember,
                            status:
                                businessMember.memberId === memberId
                                    ? BusinessMemberStatus.APPROVED
                                    : businessMember.status,
                        })),
                    );
                } else {
                    dispatch(showNotificationAsync(t('Вы не можете добавить администратора')));
                }
            } catch (error) {
                dispatch(showNotificationAsync(t('Не удалось подтвердить администратора')));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [businessId, dispatch, members, setMembers, currentUser],
    );

    const { loading } = useAsync(async () => {
        try {
            setMembers(await getBusinessMembers(businessId));
        } catch (error) {
            dispatch(showNotificationAsync(t('Не удалось загрузить администраторов')));
            setMembers([]);
        }
    }, [businessId, dispatch, setMembers]);

    return {
        members,
        onRemove,
        onApprove,
        isLoading: loading,
    };
};
